import { isEmpty } from 'lodash'

/**
 * Builds the token update data based on user type and checkout mode
 * @param {Object} params
 * @param {Object} params.checkout - Checkout state
 * @param {Object} params.portal - Portal configuration
 * @param {number|null} params.userId - Current user ID (null for guests)
 * @returns {Object} - Token update data
 */
export const buildTokenUpdateData = ({ checkout, portal, userId }) => {
  const updateData = {
    useShippingAsBilling: checkout.useShippingAsBilling
  }

  // Save billing address (top-level field, available to all users)
  if (!isEmpty(checkout.selectedBillingAddress)) {
    updateData.selectedBillingAddress = checkout.selectedBillingAddress
  }

  // Save guest-specific data (only for guest users)
  if (!userId) {
    updateData.guestCheckoutData = {
      contact_email: checkout.contactEmail || null,
      selected_customer_address: checkout.selectedCustomerAddress || null,
      guest_addresses: checkout.guestAddresses || []
    }
  } else if (portal.multiple_address_shipping) {
    // Logged-in multi-address - save selections, clear single address
    if (!isEmpty(checkout.selectedBillingAddress)) {
      updateData.selectedBillingAddress = checkout.selectedBillingAddress
    }
    if (checkout.multiAddressSelection?.length > 0) {
      updateData.multiAddressSelection = checkout.multiAddressSelection
    }
    updateData.selectedCustomerAddressId = null
  } else {
    // Logged-in single-address - save address ID, clear multi-address
    updateData.multiAddressSelection = []
    updateData.selectedCustomerAddressId = checkout.selectedCustomerAddress?.id
  }

  return updateData
}

/**
 * Builds cart items payload for validation
 * @param {Array} cartItems - Cart items from state
 * @returns {Array} - Simplified cart items for validation
 */
export const buildCartItemsForValidation = cartItems => {
  return cartItems.map(item => ({
    cart_item_id: item.cart_item_id,
    quantity: item.quantity,
    shippable_type: item.shippable_type
  }))
}

/**
 * Handles the proceed to checkout flow:
 * 1. Creates token if needed
 * 2. Updates token with current checkout state
 * 3. Runs backend validation
 *
 * @param {Object} params
 * @param {string|null} params.token - Existing checkout session token
 * @param {Object} params.checkout - Checkout state
 * @param {Array} params.cartItems - Cart items
 * @param {Object} params.portal - Portal configuration
 * @param {number|null} params.userId - Current user ID
 * @param {Function} params.createCheckoutSessionToken - Thunk to create token
 * @param {Function} params.updateCheckoutSessionToken - Thunk to update token
 * @param {Function} params.validateProceedToCheckout - Thunk to validate
 * @returns {Promise<{success: boolean, token: string|null, errors: Array|null}>}
 */
export const handleProceedToCheckout = async ({
  token,
  checkout,
  cartItems,
  portal,
  userId,
  createCheckoutSessionToken,
  updateCheckoutSessionToken,
  validateProceedToCheckout
}) => {
  let currentToken = token

  // Step 1: Create token if it doesn't exist
  if (!currentToken) {
    const tokenResult = await createCheckoutSessionToken()
    currentToken = tokenResult?.token
  }

  if (!currentToken) {
    return {
      success: false,
      token: null,
      errors: [{ message: 'Failed to create checkout session' }]
    }
  }

  // Step 2: Update token with current checkout state BEFORE validation
  const updateData = buildTokenUpdateData({ checkout, portal, userId })
  if (Object.keys(updateData).length > 0) {
    await updateCheckoutSessionToken(currentToken, updateData)
  }

  // Step 3: Run backend validation (token now has latest address)
  const cartItemsForValidation = buildCartItemsForValidation(cartItems)

  // Multi-address validation only applies to logged-in users on multi-address portals
  // Guest users always use single-address shipping
  const isMultiAddressEnabled = portal.multiple_address_shipping && !!userId
  const multiAddressSelection = isMultiAddressEnabled ? checkout.multiAddressSelection || [] : []

  const selectedCustomerAddressId = userId ? checkout.selectedCustomerAddress?.id : null

  const { valid, errors } = await validateProceedToCheckout(currentToken, {
    cartItems: cartItemsForValidation,
    multiAddressSelection,
    selectedCustomerAddressId
  })

  return {
    success: valid,
    token: currentToken,
    errors: valid ? null : errors
  }
}
